import React, { useEffect, useRef, useState, useContext } from "react";
import { createClasses, Tab, Tabs } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { PlusThinStroke, Refresh } from "@bloomberg/media-ui-icons";
import { useRegUIClient } from "@foundation/reg-ui-client";
import type {
  GetUserWatchlistsResponse,
  WatchlistInfo,
  UserPreferencesResponse,
} from "@foundation/watchlist-client";
import WatchlistAlertsDropdown from "../WatchlistAlertsDropdown";
import styles from "./styles.module.css";
import {
  fetchPortfolioData,
  fetchUserPreferences,
} from "~api/portfolio/portfolioData";
import PortfolioLoadingState from "~components/Portfolio/PortfolioLoadingState";

import {
  LOADING_STATE,
  NEW_PORTFOLIO_TAB_TITLE,
  WATCHLIST_FAILURE_TEXT,
} from "~constants/Portfolio";
import Notification from "~components/Notification";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import type { DashboardViewType, PortfolioPageData } from "~types/portfolio";
import { PageContext } from "~context/PageContext";
import { usePaywallAction } from "~hooks/usePaywallAction";

function FailureNotification(props: {
  selectedTab: string;
  onActionClick: () => void;
}) {
  return (
    <div>
      <Tabs className={styles.tabbedView} variant="default">
        {[
          <Tab
            className={styles.tabError}
            title={NEW_PORTFOLIO_TAB_TITLE}
            key={NEW_PORTFOLIO_TAB_TITLE}
            Icon={<PlusThinStroke />}
            isDefault={props.selectedTab === NEW_PORTFOLIO_TAB_TITLE}
          >
            <Notification
              text={WATCHLIST_FAILURE_TEXT}
              variant="lineBreak"
              title=""
              actionIcon={<Refresh />}
              actionText="Refresh"
              onActionClick={props.onActionClick}
            />
          </Tab>,
        ]}
      </Tabs>
    </div>
  );
}

export default function RegisteredUser() {
  const { shouldWallAction, blockUserIfWalledActionIsAttempted } =
    usePaywallAction();

  const [loadingState, setLoadingState] = useState(LOADING_STATE.LOADING);
  const [portfolioTabs, setPortfolioTabs] = useState<Array<WatchlistInfo>>([]);
  const [selectedTab, setSelectedTab] = useState("");
  const [isWatchlistAlertsToggleEnabled, setIsWatchlistAlertsToggleEnabled] =
    useState(false);

  const apiCalled = useRef(false);
  const zeroWatchlistTracked = useRef(false);
  const pageContext = useContext(PageContext);
  const tariffBundlesFeatureEnabled = Boolean(
    (pageContext?.pageData as PortfolioPageData)?.enableTariffBundles
  );
  const showBundles = tariffBundlesFeatureEnabled && !selectedTab;

  const {
    selectedPortInfo,
    portfolioInfos,
    updateSelectedPortInfo,
    updatePortfolioInfos,
    updateSelectedPortfolio,
    updateDashboardView,
  } = usePortfolioPage();

  // update selected tab when page data is updated
  useEffect(() => {
    if (!selectedPortInfo) {
      setSelectedTab(NEW_PORTFOLIO_TAB_TITLE);

      return;
    }

    setSelectedTab(String(selectedPortInfo.id));
  }, [selectedPortInfo]);

  // update tabs when page data is updated
  useEffect(() => {
    setPortfolioTabs(portfolioInfos);
  }, [portfolioInfos]);

  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";
  const subscriberType = session?.userInfo?.subscription?.type;

  useEffect(() => {
    fetchUserPreferences({ bconnectId, subscription: subscriberType ?? null })
      .then((response) => {
        const fetchedPreferences = response.data as UserPreferencesResponse;

        // set user dashboard view preference
        updateDashboardView(
          (fetchedPreferences.dashboardView || "DASH") as DashboardViewType
        );

        setIsWatchlistAlertsToggleEnabled(
          Boolean(fetchedPreferences?.addedToAlerts)
        );
      })
      .catch((_) => {
        // error handled already, ignore this to avoid further bubbling
      });
  }, [bconnectId]);

  const trackTabClick = (tabTitle: string, tabIndexClicked?: number) => {
    trackClickEvent(tabTitle.toLocaleLowerCase().replace(/\s/g, "_"));
    trackClickEvent("watchlistTabSwitch", "tab_switch", "click", "watchlist");

    if (tabTitle === NEW_PORTFOLIO_TAB_TITLE) {
      // new portfolio
      updateSelectedPortInfo(undefined, "NEW");
      updateSelectedPortfolio(undefined);

      setSelectedTab(NEW_PORTFOLIO_TAB_TITLE);

      return;
    }

    const portfolioInfo = portfolioTabs.find((portInfo, index: number) =>
      index === tabIndexClicked ? portInfo : null
    );

    if (!portfolioInfo) {
      throw new Error("Invalid Tab selection");
    }

    updateSelectedPortInfo(portfolioInfo);
  };

  const refreshPortfolios = () => {
    setLoadingState(LOADING_STATE.LOADING);

    const params = new URLSearchParams(window.location.search);
    const watchlistId = params.get("watchlist")
      ? Number(params.get("watchlist"))
      : undefined;

    fetchPortfolioData({ bconnectId, watchlistId })
      .then((result) => {
        const portfolioData = result.data as GetUserWatchlistsResponse;

        // update page portfolio infos
        updatePortfolioInfos(portfolioData.portInfos);
        // tracks if user has at least one watchlist
        if (portfolioData.portInfos.length) {
          trackClickEvent(
            "watchlistPresence",
            "user_with_watchlist",
            "uiaction",
            "watchlist"
          );
        }

        if (showBundles) {
          // if user came to the page via /watchlist-bundles url
          // set selected portfolio and selected port info to
          // undefined on first load to show bundles
          updateSelectedPortInfo(undefined, "NEW");
          updateSelectedPortfolio(undefined);
        } else {
          // update selected port info (selected tab)
          updateSelectedPortInfo(portfolioData.port.portInfo);

          // update selected portfolio
          updateSelectedPortfolio(portfolioData.port);
        }

        setLoadingState(LOADING_STATE.COMPLETE);

        if (shouldWallAction) {
          blockUserIfWalledActionIsAttempted();
        }
      })
      .catch((error) => {
        // handle client error
        if (error.status === 404) {
          // expected 404 response when user doesn't have any watchlist
          // render Empty watchlist view
          setLoadingState(LOADING_STATE.EMPTY);
        } else {
          setLoadingState(LOADING_STATE.FAILED);
        }
      });
  };

  useEffect(() => {
    // skip loading portfolio is loaded
    if (apiCalled.current) return;

    apiCalled.current = true;
    refreshPortfolios();
  }, []);

  useEffect(() => {
    if (!portfolioInfos.length && loadingState === LOADING_STATE.EMPTY) {
      if (zeroWatchlistTracked.current) return;

      zeroWatchlistTracked.current = true;

      updateSelectedPortInfo(undefined, "NEW");
      updateSelectedPortfolio(undefined);

      setSelectedTab(NEW_PORTFOLIO_TAB_TITLE);

      trackClickEvent(
        "watchlistPresence",
        "user_without_watchlist",
        "uiaction",
        "watchlist"
      );
    }
  }, [loadingState]);

  if (loadingState === LOADING_STATE.LOADING) {
    return <PortfolioLoadingState />;
  }

  // display static image when portfolioTabs is empty
  // or when an error occurs while fetching portfolio data
  if (loadingState === LOADING_STATE.FAILED) {
    return (
      <FailureNotification
        selectedTab={selectedTab}
        onActionClick={refreshPortfolios}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div
        className={createClasses([
          styles.navigationWrapper,
          isWatchlistAlertsToggleEnabled &&
            styles.navigationWrapperWithAlertsToggle,
        ])}
      >
        <Tabs
          className={styles.tabbedView}
          onTabSwitch={trackTabClick}
          showSlider
          variant="default"
        >
          {[
            ...portfolioTabs.map((portInfo) => (
              <Tab
                title={portInfo.name}
                key={portInfo.name}
                isActive={selectedTab === String(portInfo.id)}
                isDefault={selectedTab === String(portInfo.id)}
                className={styles.tabContent}
              >
                &nbsp;
              </Tab>
            )),
            <Tab
              title={NEW_PORTFOLIO_TAB_TITLE}
              key={NEW_PORTFOLIO_TAB_TITLE}
              isDefault={selectedTab === NEW_PORTFOLIO_TAB_TITLE}
              className={styles.tabContent}
              Icon={<PlusThinStroke />}
            >
              &nbsp;
            </Tab>,
          ]}
        </Tabs>
      </div>
      {isWatchlistAlertsToggleEnabled && <WatchlistAlertsDropdown />}
    </div>
  );
}
